import { removeGid } from '../utilities/handleGid';
import { logError } from '../utilities/logging';
import { Addon, ChoicePayload } from './addToCart';
import { validateAddon, validateChoice } from './atcValidation';

interface BuildPersonalisationChoicesOptions {
  choices: ChoicePayload[];
  addons: Addon[];
  baseProductConfigurationSlotId: string;
}

/**
 * Builds a personalisation choices array for cart items
 *
 * @param {BuildPersonalisationChoicesOptions} options - Configuration options
 * @returns {PersonalisationChoice[]} Array of personalisation choices
 * @throws {Error} If required parameters are missing or invalid
 */
export function buildPersonalisationChoices({
  choices = [],
  addons = [],
  baseProductConfigurationSlotId
}: BuildPersonalisationChoicesOptions) {
  // Input validation
  if (!Array.isArray(choices) || !Array.isArray(addons)) {
    throw new Error('Choices and addons must be arrays');
  }

  const choicesWithoutBaseProduct = choices.filter((choice) => {
    return choice.slotId !== baseProductConfigurationSlotId;
  });

  // Collect all validation errors
  const errors: string[] = [];

  // Validate all choices
  choicesWithoutBaseProduct.forEach((choice, index) => {
    const error = validateChoice(choice, index);
    if (error) {
      errors.push(...error);
    }
  });

  // Validate all addons
  addons.forEach((addon, index) => {
    const error = validateAddon(addon, index);
    if (error) {
      errors.push(error);
    }
  });

  // If there are validation errors, throw them all at once
  if (errors.length > 0) {
    throw new Error(`Validation errors:\n${errors.join('\n')}`);
  }

  try {
    // Build choices array
    const personalisationChoices = [
      // Map regular choices
      ...choicesWithoutBaseProduct
        // Filter out superficial choices.
        .filter((choice) => !['CHECKBOX', 'STAR_MAP', 'LOCATION_MAP'].includes(choice.slotType))
        .map(
          (choice, index) => {
            const {
              componentGroupId,
              variantId,
              slotId,
              lineItemPropertyKey,
              lineItemPropertyKeyValue,
              slotType,
              stepId,
              font,
              extraImages,
              visualisationGroup,
              supportingImage,
              supportingImageLineItemPropertyKey
            } = choice;
            // uvInsertSelection is present on UV_INSERT choices but not declared on ChoicePayload
            // (defined as a JSDoc property on a .js file — not always visible to the .ts checker).
            const uvInsertSelection = (choice as any).uvInsertSelection as
              | { variantId: number; templateId: string | null; configHandle: string; textByKey: Record<string, string>; imageByKey: Record<string, File | null> }
              | undefined;
            let choiceId = null;

            if (!['ALPHANUMERIC', 'IMAGE_UPLOAD', 'UV_INSERT'].includes(slotType) && (!componentGroupId || !variantId)) {
              logError({
                label: 'addToCart',
                error: new Error('Component group ID or variant ID is null'),
                message: 'Component group ID or variant ID is null',
                extraData: { componentGroupId, variantId },
                throwError: true
              });
            }

            switch (slotType) {
              case 'COMPONENT':
              case 'SEQUENTIAL':
                choiceId =
                  componentGroupId && variantId
                    ? `${removeGid({ id: componentGroupId })}:${removeGid({ id: variantId })}`
                    : null;
                break;
              case 'ALPHANUMERIC':
                choiceId = lineItemPropertyKeyValue;
                break;
              case 'IMAGE_UPLOAD':
                choiceId = lineItemPropertyKeyValue;
                break;
              case 'UV_INSERT': {
                // Encode as "configHandle:variantId" — the backend uses this to identify the insert.
                const sel = uvInsertSelection;
                choiceId = sel?.configHandle && sel?.variantId
                  ? `${sel.configHandle}:${sel.variantId}`
                  : '';
                break;
              }
              default:
                throw new Error(`Unknown slot type: ${slotType}`);
            }

            // Build the UV insert properties object (text LIP keys + _template_id).
            // Empty text values and missing template IDs are omitted per the AC:
            // "if text is missing but image uploaded, omit text and add image with insert SKU".
            const uvInsertProperties: Record<string, string> | null = (() => {
              if (slotType !== 'UV_INSERT' || !uvInsertSelection) return null;
              const props: Record<string, string> = {};
              for (const [key, value] of Object.entries(uvInsertSelection.textByKey || {})) {
                if (typeof value === 'string' && value.trim() !== '') {
                  props[key] = value;
                }
              }
              if (uvInsertSelection.templateId) {
                props._template_id = uvInsertSelection.templateId;
              }
              return Object.keys(props).length > 0 ? props : null;
            })();
            const uvInsertImageByKey = (() => {
              if (slotType !== 'UV_INSERT' || !uvInsertSelection) return {};
              return Object.entries(uvInsertSelection.imageByKey || {}).reduce(
                (acc, [key, value]) => {
                  if (value instanceof File) {
                    acc[key] = value;
                  }
                  return acc;
                },
                {} as Record<string, File>
              );
            })();

            return {
              choice: choiceId || '',
              slotId: removeGid({ id: slotId }),
              index: index,
              lineItemPropertyKey,
              lineItemPropertyKeyValue: lineItemPropertyKeyValue,
              slotType,
              stepId: stepId ? removeGid({ id: stepId }) : '',
              font: font,
              extraImages: extraImages || [],
              visualisationGroupId: visualisationGroup || null,
              supportingImage: supportingImage || null,
              supportingImageLineItemPropertyKey: supportingImageLineItemPropertyKey || null,
              uvInsertProperties,
              uvInsertImageByKey
            };
          }
        ),

      // Map addon choices
      ...addons.map(({ addonItemId, variantId, stepId }, index) => ({
        choice: addonItemId && variantId ? `${removeGid({ id: addonItemId })}:${removeGid({ id: variantId })}` : '',
        slotId: removeGid({ id: stepId }),
        stepId: removeGid({ id: stepId }),
        index: choices.length + index,
        lineItemPropertyKey: undefined,
        lineItemPropertyKeyValue: undefined,
        slotType: 'ADDON',
        font: undefined
      }))
    ];

    return personalisationChoices;
  } catch (error) {
    throw new Error(`Failed to build personalisation choices: ${error.message}`);
  }
}
